"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql, useQuery } from "@opensea/graphql";
import { useNumberFormatter } from "@opensea/i18n";
import { useCurrency } from "@opensea/navigation";
import { classNames, EmptyState, TimeSeriesChart } from "@opensea/ui-kit";
import { useEffect, useState } from "react";
import { VolumeFragment } from "@/components/price/Volume.fragment";
import { withSuspense } from "@/components/suspense/WithSuspense";
import { useSalesChartSubscription } from "@/hooks/api/use-sales-chart-subscription";
import { useAnalyticsTimeframeQueryParam } from "@/hooks/query-param/use-analytics-timeframe-query-param";
import { generateMovingAverage, getOutlierRange } from "../../../stats/utils";
import { SalesChartErrorState } from "./SalesChartErrorState";
import { SalesChartScatterTooltipFragment, SalesChartScatterTooltipVolumeFragment } from "./SalesChartScatterTooltip";
import { SalesChartSkeleton } from "./SalesChartSkeleton";
import { SalesChartTooltip } from "./SalesChartTooltip";
const query = graphql(`
    query SalesChartMainQuery($collectionSlug: String!, $timeframe: Timeframe!) {
      collectionChartSalesBySlug: collectionChartSalesBySlugV2(
        slug: $collectionSlug
        timeframe: $timeframe
      ) {
        ...SalesChartScatterTooltip
        collectionId
        usd
        native {
          unit
          symbol
        }
        time
        saleKey
      }
      collectionChartVolumesBySlug(
        slug: $collectionSlug
        timeframe: $timeframe
      ) {
        ...SalesChartScatterTooltipVolume
        volume {
          usd
          native {
            unit
          }
          ...Volume
        }
        time
      }
    }
  `, [SalesChartScatterTooltipFragment, SalesChartScatterTooltipVolumeFragment, VolumeFragment]);
function getBoundedSalePrice(lowerBound, upperBound, value) {
  return Math.max(lowerBound, Math.min(upperBound, value));
}
function SalesChartBase(t0) {
  const $ = _c(76);
  const {
    collectionSlug,
    className,
    onItemClicked,
    emptyState,
    errorState,
    pinOutliers: t1,
    hideSalesDots: t2,
    hideAxes: t3,
    timeframe: propTimeframe
  } = t0;
  const pinOutliers = t1 === undefined ? true : t1;
  const hideSalesDots = t2 === undefined ? false : t2;
  const hideAxes = t3 === undefined ? false : t3;
  const {
    timeframe: queryTimeframe
  } = useAnalyticsTimeframeQueryParam();
  const timeframe = propTimeframe ?? queryTimeframe;
  let t4;
  if ($[0] !== collectionSlug || $[1] !== timeframe) {
    t4 = {
      query,
      variables: {
        collectionSlug,
        timeframe
      }
    };
    $[0] = collectionSlug;
    $[1] = timeframe;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [t5] = useQuery(t4);
  const {
    data,
    error
  } = t5;
  let t6;
  if ($[3] !== data?.collectionChartSalesBySlug) {
    t6 = data?.collectionChartSalesBySlug || [];
    $[3] = data?.collectionChartSalesBySlug;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const [sales, setSales] = useState(t6);
  let t7;
  if ($[5] !== data?.collectionChartSalesBySlug) {
    t7 = () => {
      setSales(data?.collectionChartSalesBySlug || []);
    };
    $[5] = data?.collectionChartSalesBySlug;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = data?.collectionChartSalesBySlug;
  let t9;
  if ($[7] !== t8) {
    t9 = [t8];
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  useEffect(t7, t9);
  useSalesChartSubscription(collectionSlug, setSales);
  const {
    currency
  } = useCurrency();
  const format = useNumberFormatter();
  let t10;
  if ($[9] !== currency) {
    t10 = sale => currency === "crypto" ? sale.native.unit : sale.usd;
    $[9] = currency;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const t11 = timeframe === "ALL_TIME" ? 3 : 1.5;
  let lowerBound;
  let t12;
  let upperBound;
  let volumes;
  if ($[11] !== currency || $[12] !== data?.collectionChartVolumesBySlug || $[13] !== pinOutliers || $[14] !== sales || $[15] !== t10 || $[16] !== t11 || $[17] !== timeframe) {
    const outlierRange = getOutlierRange(sales, t10, t11);
    lowerBound = Math.max(outlierRange?.lowerBound ?? 0, 0);
    upperBound = outlierRange?.upperBound ?? Number.POSITIVE_INFINITY;
    let t13;
    if ($[22] !== data?.collectionChartVolumesBySlug) {
      t13 = data?.collectionChartVolumesBySlug ?? [];
      $[22] = data?.collectionChartVolumesBySlug;
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    volumes = t13;
    const getMovingAverageMap = function getMovingAverageMap() {
      let window = 20;
      if (timeframe === "ONE_HOUR" || timeframe === "ONE_DAY") {
        window = 7;
      } else {
        if (timeframe === "SEVEN_DAYS") {
          window = 12;
        } else {
          if (timeframe === "THIRTY_DAYS") {
            window = 15;
          } else {
            if (timeframe === "ONE_YEAR") {
              window = 20;
            } else {
              if (timeframe === "ALL_TIME") {
                window = 25;
              }
            }
          }
        }
      }
      return generateMovingAverage(sales.map(item => {
        const value = currency === "crypto" ? item.native.unit : item.usd;
        const price = pinOutliers ? getBoundedSalePrice(lowerBound, upperBound, value) : value;
        return {
          id: item.saleKey,
          price
        };
      }), window);
    };
    t12 = getMovingAverageMap();
    $[11] = currency;
    $[12] = data?.collectionChartVolumesBySlug;
    $[13] = pinOutliers;
    $[14] = sales;
    $[15] = t10;
    $[16] = t11;
    $[17] = timeframe;
    $[18] = lowerBound;
    $[19] = t12;
    $[20] = upperBound;
    $[21] = volumes;
  } else {
    lowerBound = $[18];
    t12 = $[19];
    upperBound = $[20];
    volumes = $[21];
  }
  const movingAverageMap = t12;
  if (sales.length === 0) {
    let t13;
    if ($[24] !== emptyState) {
      t13 = emptyState ?? <EmptyState className="pb-4" variant="no-results" />;
      $[24] = emptyState;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    return t13;
  }
  if (error) {
    let t13;
    if ($[26] !== errorState) {
      t13 = errorState ?? <EmptyState className="pb-4" variant="no-data" />;
      $[26] = errorState;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    return t13;
  }
  let t13;
  if ($[28] !== className) {
    t13 = classNames("h-[400px]", className);
    $[28] = className;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const chartClassName = t13;
  let t14;
  if ($[30] !== currency || $[31] !== lowerBound || $[32] !== upperBound) {
    t14 = item_1 => {
      const value_0 = currency === "crypto" ? item_1.native.unit : item_1.usd;
      return value_0 < lowerBound || value_0 > upperBound;
    };
    $[30] = currency;
    $[31] = lowerBound;
    $[32] = upperBound;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== currency || $[35] !== hideSalesDots || $[36] !== lowerBound || $[37] !== pinOutliers || $[38] !== upperBound) {
    t15 = hideSalesDots ? undefined : item_2 => {
      const value_1 = currency === "crypto" ? item_2.native.unit : item_2.usd;
      return pinOutliers ? getBoundedSalePrice(lowerBound, upperBound, value_1) : value_1;
    };
    $[34] = currency;
    $[35] = hideSalesDots;
    $[36] = lowerBound;
    $[37] = pinOutliers;
    $[38] = upperBound;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== movingAverageMap) {
    t16 = item_3 => {
      try {
        return movingAverageMap[item_3.saleKey];
      } catch {
        return 0;
      }
    };
    $[40] = movingAverageMap;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const t17 = `${timeframe}-${String(pinOutliers)}-${String(hideSalesDots)}-${sales[0]?.time ?? ""}-${sales.at(-1)?.time ?? ""}`;
  const t18 = pinOutliers ? lowerBound : undefined;
  const t19 = pinOutliers ? upperBound : undefined;
  let t20;
  if ($[42] !== currency || $[43] !== format || $[44] !== sales[0]) {
    t20 = value_2 => format(value_2, {
      display: currency === "crypto" ? "quantity" : "usd",
      suffix: currency === "crypto" ? sales[0].native.symbol : undefined
    });
    $[42] = currency;
    $[43] = format;
    $[44] = sales[0];
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== hideAxes || $[47] !== t18 || $[48] !== t19 || $[49] !== t20) {
    t21 = {
      hidden: hideAxes,
      yAxisMin: t18,
      yAxisMax: t19,
      tickFormat: t20
    };
    $[46] = hideAxes;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== hideAxes) {
    t22 = {
      hidden: hideAxes
    };
    $[51] = hideAxes;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t21 || $[54] !== t22) {
    t23 = {
      YAxis: t21,
      XAxis: t22
    };
    $[53] = t21;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== movingAverageMap || $[57] !== onItemClicked || $[58] !== timeframe) {
    t24 = props => <SalesChartTooltip movingAverageMaps={movingAverageMap} onItemClicked={onItemClicked} timeframe={timeframe} {...props} />;
    $[56] = movingAverageMap;
    $[57] = onItemClicked;
    $[58] = timeframe;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      tooltipDelay: 200
    };
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== currency) {
    t26 = v_0 => currency === "crypto" ? v_0.volume.native.unit : v_0.volume.usd;
    $[61] = currency;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== t26 || $[64] !== volumes) {
    t27 = {
      items: volumes,
      getDate: _temp2,
      getValue: t26
    };
    $[63] = t26;
    $[64] = volumes;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== chartClassName || $[67] !== sales || $[68] !== t14 || $[69] !== t15 || $[70] !== t16 || $[71] !== t17 || $[72] !== t23 || $[73] !== t24 || $[74] !== t27) {
    t28 = <TimeSeriesChart className={chartClassName} data={sales} getDate={_temp} getIsOutlier={t14} getScatterValue={t15} getValue={t16} key={t17} overrides={t23} renderTooltip={t24} scatterOptions={t25} standaloneColumn={t27} />;
    $[66] = chartClassName;
    $[67] = sales;
    $[68] = t14;
    $[69] = t15;
    $[70] = t16;
    $[71] = t17;
    $[72] = t23;
    $[73] = t24;
    $[74] = t27;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  return t28;
}
function _temp2(v) {
  return new Date(v.time);
}
function _temp(item_0) {
  return new Date(item_0.time);
}
export const SalesChart = withSuspense(SalesChartBase, {
  fallback: SalesChartSkeleton,
  errorFallback: <SalesChartErrorState />
});